import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../context/Settings';
import { getCountryName, LanguageCodes, supportedLanguages } from '../../i18n/i18n';
import { Icon } from '../Layout/Icon/Icon';

const Dropdown = () => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldPositionAbove, setShouldPositionAbove] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { settings, saveSettings } = useSettings();

  // Temporary - danish version is blocked. Uncomment first line, delete second when all language versions are ready
  // const options = supportedLanguages.filter(el => el !== 'en');
  const options = supportedLanguages.filter((el) => ['sv',/* 'nl',*/ 'en', 'de'/*, 'es'*/].includes(el));

  const toggleList = () => {
    if (isListOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsListOpen(false);
        setIsClosing(false);
      }, 200);
    } else {
      setIsListOpen(true);
    }
  };

  const selectLang = (lang: string) => {
    i18n.changeLanguage(lang);

    saveSettings({ language: lang as LanguageCodes });
    navigate(`/${lang}/home`, { replace: true });
  };

  const selectOverrideStyle = settings.isSafari ? { paddingTop: '4px' } : {};

  useEffect(() => {
    if (isListOpen && dropdownRef.current && listRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const listRect = listRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const spaceBelow = windowHeight - dropdownRect.bottom;
      const listHeight = listRect.height;

      const needsRepositioning = spaceBelow < listHeight && dropdownRect.top > listHeight;
      setShouldPositionAbove(needsRepositioning);
    }
  }, [isListOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isListOpen) {
          setIsClosing(true);
          setTimeout(() => {
            setIsListOpen(false);
            setIsClosing(false);
          }, 200);
        }
      }
    };

    if (isListOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isListOpen]);

  return (
    <div className="dd-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className={`dd-header ${shouldPositionAbove ? 'dd-header--above' : ''}`}
        onClick={toggleList}
      >
        <div className="dd-header__title" style={selectOverrideStyle}>
          {'Select your country'}
          <Icon
            name="rectangle"
            stroke="#1E223C"
            fill="none"
            size={20}
            className={`dd-arrow ${isListOpen ? 'dd-arrow--expanded' : ''} ${shouldPositionAbove ? 'dd-arrow--above' : ''}`}
          />
        </div>
      </button>
      {isListOpen && (
        <div
          role="list"
          className={`dd-list ${shouldPositionAbove ? 'dd-list--above' : ''} ${isClosing ? 'dd-list--closing' : ''}`}
          ref={listRef}
        >
          {options.map((item: LanguageCodes) => (
            <button
              type="button"
              className="dd-list-item"
              style={selectOverrideStyle}
              key={item}
              onClick={() => selectLang(item)}
            >
              {getCountryName(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
